import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { StatisticTypeEnum } from 'src/app/enum/statistic-type.enum';
import { LiteOfferResult } from 'src/app/models/configurators-2d/tunnel/kitchen/kitchen-tunnel-store-model';
import { CarpentryService } from 'src/app/services/configurators-2d/applications/carpentry.service';
import { StatisticService } from 'src/app/services/statistic.service';
import { CarpentryConfigurationData } from 'src/app/stores/tunnel/model/configuration-data/carpentry-configuration-data';
import { PricingState, FundingState, Funding } from 'src/app/stores/tunnel/model/tunnel-data-state';
import { TunnelDataStore } from 'src/app/stores/tunnel/tunnel-data.store';
import { TunnelNavigationStore } from 'src/app/stores/tunnel/tunnel-navigation.store';
import { BaseTunnelObject } from '../../model/base-tunnel-object';
import { Configuration } from 'src/app/models/configurators-2d/applications/kitchen/configuration';
import { FeatureSettingsService } from 'src/app/services/feature-settings/feature-settings.service';
import { isFundingEmpty, isFundingStateEmpty } from 'src/app/services/helpers/funding.helper';

type Column = {
  header: string;
  field: string;
};

@Component({
  selector: 'ng-casto-carpentry-tunnel-funding',
  templateUrl: './carpentry-tunnel-funding.component.html',
})
export class CarpentryTunnelFundingComponent extends BaseTunnelObject implements OnInit, OnDestroy {
  // Services
  protected carpentryService = inject(CarpentryService);
  private readonly statisticService = inject(StatisticService);
  protected featureSettingsService = inject(FeatureSettingsService);

  // Properties
  protected currentPricing: PricingState;
  protected currentFunding: FundingState;
  protected currentWithInstallation: boolean = null;
  protected loading: boolean = false;
  protected pricingLoading: boolean = false;
  protected downPayment: number = 0;
  private carpentryConfiguration: CarpentryConfigurationData;

  protected readonly offersColumns: Column[] = [
    { header: 'Barème', field: 'code' },
    { header: 'Offre', field: 'offer' },
    { header: 'Mensualité', field: 'mensuality' },
    { header: 'Durée', field: 'duration' },
    { header: 'Total intérêts', field: 'interest' },
    { header: 'Total dû', field: 'total' },
    { header: 'TAEG', field: 'taeg' },
  ];

  constructor() {
    const tunnelNavigationStore = inject(TunnelNavigationStore);
    const tunnelDataStore = inject(TunnelDataStore);
    super(tunnelNavigationStore, tunnelDataStore);

    this.tunnelNavigationStore = tunnelNavigationStore;
    this.tunnelDataStore = tunnelDataStore;
  }

  get disableInstallation(): boolean {
    return this.carpentryService.isInstallationDisabled();
  }

  get offers(): LiteOfferResult[] {
    if (!this.currentFunding) {
      return [];
    }
    return this.funding.offerTable;
  }

  get funding(): Funding {
    if (this.currentFunding) {
      return this.currentFunding[
        this.currentWithInstallation ? 'fundingWithInstallation' : 'fundingWithoutInstallation'
      ];
    }
  }

  get offersWithFooter() {
    return this.offers.length > 0 ? [...this.offers, { isTextContainer: true }] : [];
  }

  ngOnInit(): void {
    super.ngOnInit();
    void this.initFundingStep();
  }

  private async initFundingStep(): Promise<void> {
    await this.saveProjectIfNeeded();
    // Restaure l'apport persisté avant un éventuel recalcul : `computeFunding` envoie
    // `this.downPayment`, et l'API n'utilise l'apport enregistré que si le paramètre est nul —
    // le lire après coup écraserait donc l'apport du vendeur par 0.
    this.downPayment = this.currentFunding?.downPayment ?? 0;
    await this.initFundingIfNeeded();
    this.downPayment = this.currentFunding?.downPayment ?? this.downPayment;
    // await this.sendAccessFundingStatistic();
  }

  /** Sauvegarde le projet si la liste d'achat a été modifiée sans passer par le bouton "Continuer",
   *  afin que le backend calcule toujours le financement sur les données à jour. */
  private async saveProjectIfNeeded(): Promise<void> {
    if (this.carpentryService.configuration?.configurationHasChanged) {
      await this.carpentryService.saveProject();
    }
  }

  async sendAccessFundingStatistic() {
    //Envoi de la stat d'accès à la page de simulation de financement
    this.statisticService.sendStatistic(
      StatisticTypeEnum.FUNDING_COUNT,
      1,
      CONFIG_CONTEXT.CARPENTRY,
      this.carpentryConfiguration.configurationId
    );
  }

  protected isNoOffer(): boolean {
    return isFundingEmpty(this.funding);
  }

  get fundingAmount(): number {
    return Math.max((this.currentPricing?.totalWithoutFunding?.actualAmount ?? 0) - (this.downPayment ?? 0), 0);
  }

  protected subscribeToObservable() {
    this.subscriptionList.push(
      this.tunnelDataStore.getCurrentPricing().subscribe(currentPricing => {
        if (currentPricing) {
          this.currentPricing = currentPricing;
        }
      }),
      this.tunnelDataStore.getCurrentFunding().subscribe(currentFunding => {
        if (currentFunding) {
          this.currentFunding = currentFunding;
        }
      }),

      this.tunnelDataStore.getCurrentWithInstallation().subscribe(async currentWithInstallation => {
        if (currentWithInstallation !== undefined && this.currentWithInstallation !== currentWithInstallation) {
          let lastCurrentValue = this.currentWithInstallation;
          this.currentWithInstallation = currentWithInstallation;
          //Nathan - 08/07 : Ces lignes étaient commentées par Benjamin, surement pour essayer d'opti les appels
          if (lastCurrentValue !== null) {
            await this.updateFunding();
          }
        }
      }),
      this.tunnelDataStore.getConfigurationData().subscribe(configurationData => {
        if (configurationData) {
          this.carpentryConfiguration = configurationData.get(CONFIG_CONTEXT.CARPENTRY) as CarpentryConfigurationData;
        }
      }),
      this.tunnelDataStore.getPricingLoading().subscribe(loading => {
        this.pricingLoading = loading;
      })
    );
  }

  private async computeFunding() {
    await this.carpentryService.computeFunding({
      tunnelConfigurationId: (this.carpentryConfiguration as Configuration).tunnelId,
      downPaymentAmount: this.downPayment,
      shouldRecalculateFinancing: true,
    });
  }

  protected async updateFunding() {
    try {
      this.loading = true;
      const financementEnabled = await this.featureSettingsService.isFinancementEnabled();

      if (!financementEnabled) {
        await this.ensurePricingIfNeeded();
        this.tunnelDataStore.disableCurrentFunding();
        this.checkDownPayment();
        return;
      }

      await this.computeFunding();
      this.checkDownPayment();
    } catch (error) {
      this.loading = false;
    } finally {
      this.loading = false;
    }
  }

  private async initFundingIfNeeded() {
    const financementEnabled = await this.featureSettingsService.isFinancementEnabled();

    if (!financementEnabled) {
      await this.ensurePricingIfNeeded();
      this.tunnelDataStore.disableCurrentFunding();
      return;
    }

    // Remplace le verrou `retryInOffer` supprimé : le prédicat partagé reconnaît désormais la ligne
    // sentinelle « Aucune offre », et l'appel se fait une fois au montage plutôt qu'à chaque émission
    // du store — plus besoin de latch anti-boucle.
    if (isFundingStateEmpty(this.tunnelDataStore.getCurrentStateValue().currentFunding)) {
      await this.updateFunding();
    }
  }

  private async ensurePricingIfNeeded(): Promise<void> {
    if (!this.currentPricing?.totalWithoutFunding) {
      await this.carpentryService.loadCarpentryPricing();
    }
  }

  private checkDownPayment() {
    if (this.downPayment > (this.currentPricing?.totalWithoutFunding?.actualAmount ?? 0)) {
      this.downPayment = this.currentPricing?.totalWithoutFunding?.actualAmount ?? 0;
    }
  }

  protected async setWithInstallation(withInstallation: boolean) {
    try {
      this.loading = true;
      await this.carpentryService.updateWithInstallation(withInstallation);
    } catch (error) {
      this.loading = false;
    } finally {
      this.loading = false;
    }
    this.tunnelDataStore.setWithInstallation(withInstallation);
  }

  protected isOfferSelected(offer: LiteOfferResult): boolean {
    if (offer) {
      return (
        this.funding.mensuality === offer.mensuality &&
        this.funding.duration === offer.duration &&
        this.funding.code === offer.code
      );
    }
  }

  protected async selectOffer(offer: LiteOfferResult) {
    try {
      this.loading = true;
      await this.carpentryService.selectOffer(offer.code, offer.duration);
    } catch (error) {
      this.loading = false;
    } finally {
      this.loading = false;
    }
  }
}
